import { FunctionComponent, MouseEvent, PropsWithChildren } from "react";

import { format } from "date-fns";

import { faBuilding, faUser } from "@fortawesome/pro-solid-svg-icons";

import { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";

import { ensureHttpProtocol } from "@/utils/url";

import { CrmIcon, rendersCrmIcon } from "../../../../CrmIcon";

type Prospect = NonNullable<ListPerson["prospect"]>;
type CrmProspect = NonNullable<Prospect["crmProspects"]>[number];
type CrmOrganization = NonNullable<
	NonNullable<Prospect["organization"]>["crmOrganizations"]
>[number];

const stopPropagation = (e: MouseEvent) => e.stopPropagation();

// Logos render desaturated/dimmed and regain full color on hover (mockup behavior).
const logoClassName =
	"flex items-center grayscale opacity-60 transition-[filter,opacity] hover:grayscale-0 hover:opacity-100";

const SyncGroup: FunctionComponent<PropsWithChildren<{ icon: IconDefinition; label: string }>> = ({
	icon,
	label,
	children,
}) => {
	return (
		<div className="flex flex-row items-center gap-1 border border-border-light rounded-md px-1.5 py-0.5">
			<Tooltip content={label}>
				<Icon className="w-2.5 h-2.5 text-text-meta" icon={icon} />
			</Tooltip>
			{children}
		</div>
	);
};

const CrmProspectTooltipContent: FunctionComponent<{
	crmProspect: CrmProspect;
	row: ListPerson;
}> = ({ crmProspect, row }) => {
	return (
		<div className="flex flex-col gap-2">
			<span>Current Status: {crmProspect.currentStatus}</span>
			{crmProspect.lastUpdatedAt && (
				<span>
					Last Updated At: {format(new Date(crmProspect.lastUpdatedAt), "HH:mm dd.MM.yyyy")}
				</span>
			)}
			<span>Account Owner: {crmProspect.accountOwner}</span>
			<span>Account Owner ID: {crmProspect.accountOwnerId}</span>
			<span>Reference ID: {crmProspect.referenceId}</span>
			{crmProspect.customField1 && <span>Custom Field 1: {crmProspect.customField1}</span>}
			{crmProspect.customField2 && <span>Custom Field 2: {crmProspect.customField2}</span>}
			{row.prospect?.crmDeals && row.prospect.crmDeals.length > 0 && (
				<>
					<hr className="border-t border-gray-500 my-1" />
					<span className="font-semibold">Deals</span>
					{row.prospect.crmDeals.map((deal) => (
						<div key={deal.id} className="flex flex-col gap-1 ml-2">
							<span className="font-medium">
								{deal.url ? (
									<a
										className="text-inherit underline"
										href={deal.url}
										rel="noreferrer"
										target="_blank"
									>
										{deal.dealName}
									</a>
								) : (
									deal.dealName
								)}
							</span>
							{deal.pipeline && <span>Pipeline: {deal.pipeline}</span>}
							{deal.dealStage && <span>Stage: {deal.dealStage}</span>}
							{deal.dealOwner && <span>Owner: {deal.dealOwner}</span>}
							{deal.closeDate && (
								<span>Close Date: {format(new Date(deal.closeDate), "dd.MM.yyyy")}</span>
							)}
							{deal.hubspotSyncStatus && <span>Sync Status: {deal.hubspotSyncStatus}</span>}
						</div>
					))}
				</>
			)}
		</div>
	);
};

const CrmProspectRecord: FunctionComponent<{ crmProspect: CrmProspect; row: ListPerson }> = ({
	crmProspect,
	row,
}) => {
	const hasDeal = (row.prospect?.crmDeals?.length ?? 0) > 0;

	return (
		<Tooltip
			content={<CrmProspectTooltipContent crmProspect={crmProspect} row={row} />}
			disabled={!crmProspect.url}
		>
			{crmProspect.url ? (
				<a
					className={logoClassName}
					href={ensureHttpProtocol(crmProspect.url)}
					rel="noreferrer"
					target="_blank"
					onClick={stopPropagation}
				>
					<CrmIcon
						crmType={crmProspect.crmType}
						hasDeal={hasDeal}
						syncError={crmProspect.syncError}
						syncStatus={crmProspect.syncStatus}
						url={crmProspect.url}
					/>
				</a>
			) : (
				<span className={logoClassName}>
					<CrmIcon
						crmType={crmProspect.crmType}
						hasDeal={hasDeal}
						syncError={crmProspect.syncError}
						syncStatus={crmProspect.syncStatus}
						url={crmProspect.url ?? ""}
					/>
				</span>
			)}
		</Tooltip>
	);
};

const CrmOrganizationRecord: FunctionComponent<{ crmOrganization: CrmOrganization }> = ({
	crmOrganization,
}) => {
	return (
		<Tooltip
			content={
				<div className="flex flex-col gap-2">
					{crmOrganization.lastUpdatedAt ? (
						<span>
							Last Updated At: {format(new Date(crmOrganization.lastUpdatedAt), "HH:mm dd.MM.yyyy")}
						</span>
					) : (
						<></>
					)}
					<span>Account Owner: {crmOrganization.accountOwner}</span>
					<span>Account Owner ID: {crmOrganization.accountOwnerId}</span>
					<span>Reference ID: {crmOrganization.referenceId}</span>
					{crmOrganization.customField1 && (
						<span>Custom Field 1: {crmOrganization.customField1}</span>
					)}
					{crmOrganization.customField2 && (
						<span>Custom Field 2: {crmOrganization.customField2}</span>
					)}
				</div>
			}
		>
			<a
				className={logoClassName}
				href={ensureHttpProtocol(crmOrganization.url ?? "")}
				rel="noreferrer"
				target="_blank"
				onClick={stopPropagation}
			>
				<CrmIcon
					crmType={crmOrganization.crmType}
					syncError={crmOrganization.syncError}
					syncStatus={crmOrganization.syncStatus}
					url={crmOrganization.url}
				/>
			</a>
		</Tooltip>
	);
};

/**
 * Right-aligned CRM sync section of the lead card (mockup `lead-card-mockup-466px.html`):
 * a small "Sync" label followed by one bordered group per record kind — contact records
 * (person prefix icon, one CRM logo per `prospect.crmProspects`) and company records
 * (building prefix icon, one CRM logo per `organization.crmOrganizations`).
 */
export const CrmSyncs: FunctionComponent<{ row: ListPerson }> = ({ row }) => {
	const { t } = useTranslation();

	// Skip records CrmIcon resolves to nothing (no url/crmType/sync state) — they would
	// otherwise produce an empty bordered group or invisible-but-spaced entries.
	const crmProspects = (row.prospect?.crmProspects ?? []).filter(rendersCrmIcon);
	const crmOrganizations = (row.prospect?.organization?.crmOrganizations ?? []).filter(
		rendersCrmIcon
	);

	if (crmProspects.length === 0 && crmOrganizations.length === 0) {
		return null;
	}

	return (
		<div className="ml-auto flex flex-row items-center gap-1.5 shrink-0 self-center">
			<span className="text-xxs text-text-meta select-none">{t("messages.crmSyncLabel")}</span>
			{crmProspects.length > 0 && (
				<SyncGroup icon={faUser} label={t("messages.crmSyncContactRecords")}>
					{crmProspects.map((crmProspect) => (
						<CrmProspectRecord key={crmProspect.id} crmProspect={crmProspect} row={row} />
					))}
				</SyncGroup>
			)}
			{crmOrganizations.length > 0 && (
				<SyncGroup icon={faBuilding} label={t("messages.crmSyncCompanyRecords")}>
					{crmOrganizations.map((crmOrganization) => (
						<CrmOrganizationRecord key={crmOrganization.id} crmOrganization={crmOrganization} />
					))}
				</SyncGroup>
			)}
		</div>
	);
};
